import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { PageContainer } from '../app/AppLayout';
import {
  queryKeys,
  useActiveRuns,
  useBlueprint,
  useBlueprintPage,
  useDeploymentList,
  usePager,
  useProviders,
} from '../api/queries';
import type {
  DeploymentListFilter,
  DeploymentListItem,
  DeploymentStatus,
  DriftState,
  RunSummary,
} from '../api/types';
import { deploymentStatusPresentation, driftPresentation } from '../components/presentation';
import { ActionButton, ErrorMessage, Loading, PageHeader, Pager, StatusBadge } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { listLimit, withParam } from '../lib/listParams';
import { ListActivityWindow } from '../runs/ActivityWindow';
import { strings } from '../strings';
import { CreateDeploymentDialog } from './CreateDeploymentDialog';
import { useCardTable } from '../lib/cardTable';

const s = strings.deployments;
const STATUSES: readonly DeploymentStatus[] = ['new', 'active', 'failed', 'destroyed'];
const DRIFTS: readonly DriftState[] = ['unknown', 'none', 'detected'];

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

/** The search field: the URL's `q`, sent 300 ms after typing stops. `/` focuses it (spec 6.1.12). */
function SearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [text, setText] = useState(value);
  const id = useId();
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  // A new value from the URL (back, a link) replaces what was typed.
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    setShown(value);
    setText(value);
  }
  useEffect(() => {
    if (text === value) return;
    const timer = setTimeout(() => onChangeRef.current(text), 300);
    return () => clearTimeout(timer);
  }, [text, value]);
  return (
    <div className="search-field">
      <label htmlFor={id} className="sr-only">
        {s.search}
      </label>
      <input
        id={id}
        type="search"
        className="input"
        placeholder={s.search}
        value={text}
        data-list-search
        aria-keyshortcuts="/"
        onChange={(e) => setText(e.target.value)}
      />
      <kbd aria-hidden className="kbd">
        /
      </kbd>
    </div>
  );
}

function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: readonly { value: T; label: string }[];
  onChange: (value: T | null) => void;
}) {
  const id = useId();
  return (
    <div className="filter">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="input"
        value={value ?? ''}
        onChange={(e) => onChange((e.target.value || null) as T | null)}
      >
        <option value="">{strings.common.any}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ActiveRun({
  deployment,
  active,
}: {
  deployment: DeploymentListItem;
  active: Map<string, RunSummary>;
}) {
  if (!deployment.activeRunId) return <>{strings.common.dash}</>;
  const run = active.get(deployment.activeRunId);
  if (!run) {
    return (
      <Link className="link" to={`/runs/${deployment.activeRunId}`}>
        {s.openRun}
      </Link>
    );
  }
  const progress =
    run.progress && run.progress.total > 0
      ? strings.shell.activeRuns.progress(run.progress.completed, run.progress.total)
      : null;
  return (
    <Link className="link" to={`/runs/${run.id}`}>
      {s.activeRunLabel(strings.status.runKind[run.kind], progress)}
    </Link>
  );
}

function DeploymentsTable({ items, showBlueprint }: { items: DeploymentListItem[]; showBlueprint: boolean }) {
  const cardTable = useCardTable();
  const activeRuns = useActiveRuns();
  const active = useMemo(
    () => new Map((activeRuns.data?.items ?? []).map((run) => [run.id, run])),
    [activeRuns.data],
  );
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table ref={cardTable} className="table table-cards">
        <thead>
          <tr>
            <th scope="col">{s.name}</th>
            {showBlueprint && <th scope="col">{s.blueprint}</th>}
            <th scope="col">{s.status}</th>
            <th scope="col">{s.drift}</th>
            <th scope="col">{s.providers}</th>
            <th scope="col">{s.version}</th>
            <th scope="col">{s.lastApply}</th>
            <th scope="col">{s.activeRun}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((deployment) => {
            const providers = Object.keys(deployment.settings.cloudAccounts ?? {}).sort();
            const applied = deployment.currentBlueprintVersion;
            return (
              <tr key={deployment.id}>
                <td>
                  <Link className="link font-medium" to={`/deployments/${deployment.id}`}>
                    {deployment.name}
                  </Link>
                </td>
                {showBlueprint && (
                  <td>
                    <Link className="link" to={`/blueprints/${deployment.blueprintId}`}>
                      {deployment.blueprintName}
                    </Link>
                  </td>
                )}
                <td>
                  <StatusBadge presentation={deploymentStatusPresentation[deployment.status]} />
                </td>
                <td>
                  <StatusBadge presentation={driftPresentation[deployment.drift]} />
                </td>
                <td>{providers.length ? providers.join(', ') : strings.common.dash}</td>
                <td>
                  {applied !== null ? `v${applied}` : strings.common.dash}
                  {applied !== null && applied < deployment.blueprintLatestVersion && (
                    <span className="badge tone-info ml-2">{s.updateAvailable}</span>
                  )}
                </td>
                <td title={formatDateTime(deployment.lastSuccessfulApply?.completedAt)}>
                  {formatRelative(deployment.lastSuccessfulApply?.completedAt)}
                </td>
                <td>
                  <ActiveRun deployment={deployment} active={active} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DeploymentsList({
  filter,
  limit,
  showBlueprint,
  filtered,
}: {
  filter: DeploymentListFilter;
  limit: number;
  showBlueprint: boolean;
  filtered: boolean;
}) {
  const pager = usePager();
  const page = useDeploymentList(filter, pager.cursor, limit);
  if (page.error) return <ErrorMessage error={page.error} onRetry={() => void page.refetch()} />;
  if (!page.data) return <Loading />;
  return (
    <>
      {page.data.items.length === 0 ? (
        filtered || pager.hasPrevious ? (
          <p className="help">{s.noMatch}</p>
        ) : (
          <p className="help">
            {showBlueprint ? (
              <>
                {s.emptyAll}{' '}
                <Link className="link" to="/blueprints">
                  {s.goToBlueprints}
                </Link>
              </>
            ) : (
              s.empty
            )}
          </p>
        )
      ) : (
        <DeploymentsTable items={page.data.items} showBlueprint={showBlueprint} />
      )}
      <Pager
        hasPrevious={pager.hasPrevious}
        nextCursor={page.data.nextCursor}
        onPrevious={pager.previous}
        onNext={pager.next}
      />
    </>
  );
}

/**
 * `/deployments` (all blueprints) and `/blueprints/:id/deployments` (one blueprint), spec 6.4: one request per
 * page to `GET /deployments`, filters in the URL.
 */
export function DeploymentsPage() {
  const { id: blueprintId } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // `?new=1` (the designer's "New deployment…", spec 6.3) opens the create dialog.
  const [creating, setCreating] = useState(() => !!blueprintId && search.get('new') === '1');
  const blueprint = useBlueprint(blueprintId ?? '', !!blueprintId);
  const blueprints = useBlueprintPage(null);
  const providers = useProviders();

  const filter = useMemo<DeploymentListFilter>(
    () => ({
      blueprintId: blueprintId ?? (search.get('blueprint') || null),
      status: oneOf(search.get('status'), STATUSES),
      drift: oneOf(search.get('drift'), DRIFTS),
      provider: search.get('provider') || null,
      q: search.get('q') || null,
    }),
    [blueprintId, search],
  );
  const limit = listLimit(search.get('limit'));
  const filtered = !!(
    filter.status ||
    filter.drift ||
    filter.provider ||
    filter.q ||
    (!blueprintId && filter.blueprintId)
  );
  const setParam = (name: string, value: string | null) =>
    setSearch(withParam(search, name, value), { replace: true });

  return (
    <PageContainer>
      <PageHeader
        title={s.title}
        actions={
          blueprintId && (
            <ActionButton
              permission="operate"
              variant="primary"
              icon={<Plus aria-hidden className="size-4" />}
              onClick={() => setCreating(true)}
            >
              {s.create}
            </ActionButton>
          )
        }
      >
        <p className="mt-0.5 text-fg-muted">
          {blueprintId ? (
            <>
              {s.blueprintSubtitle}{' '}
              <Link className="link" to={`/blueprints/${blueprintId}`}>
                {blueprint.data?.data.name ?? ''}
              </Link>
            </>
          ) : (
            s.listSubtitle
          )}
        </p>
      </PageHeader>

      <div role="group" aria-label={s.filters} className="list-toolbar">
        <SearchField value={filter.q ?? ''} onChange={(q) => setParam('q', q.trim() || null)} />
        {!blueprintId && (
          <FilterSelect
            label={s.blueprintFilter}
            value={filter.blueprintId ?? null}
            options={(blueprints.data?.items ?? []).map((b) => ({ value: b.id, label: b.name }))}
            onChange={(value) => setParam('blueprint', value)}
          />
        )}
        <FilterSelect
          label={s.statusFilter}
          value={filter.status ?? null}
          options={STATUSES.map((value) => ({ value, label: strings.status.deployment[value] }))}
          onChange={(value) => setParam('status', value)}
        />
        <FilterSelect
          label={s.driftFilter}
          value={filter.drift ?? null}
          options={DRIFTS.map((value) => ({ value, label: strings.status.drift[value] }))}
          onChange={(value) => setParam('drift', value)}
        />
        <FilterSelect
          label={s.providerFilter}
          value={filter.provider ?? null}
          options={(providers.data ?? []).map((p) => ({ value: p.key, label: p.displayName }))}
          onChange={(value) => setParam('provider', value)}
        />
      </div>

      <DeploymentsList
        key={JSON.stringify([filter, limit])}
        filter={filter}
        limit={limit}
        showBlueprint={!blueprintId}
        filtered={filtered}
      />

      {creating && blueprintId && (
        <CreateDeploymentDialog
          blueprintId={blueprintId}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            void queryClient.invalidateQueries({ queryKey: ['deployments'] });
            void queryClient.invalidateQueries({ queryKey: ['blueprints'] });
            queryClient.setQueryData(queryKeys.deployment(created.data.id), created);
            navigate(`/deployments/${created.data.id}`);
          }}
        />
      )}
      <ListActivityWindow />
    </PageContainer>
  );
}
